/**
 * Spese sul telefono (7/10/2026, rifatta il 26/10/2026 come il computer: SpesePage.tsx).
 * Dal 26/10: una pagina sola, senza le tre pillole. In cima quanto c'è da pagare con un bonifico
 * nostro; poi "Da pagare", gli addebiti automatici passati da spuntare, le fatture arrivate su
 * FattureInCloud e ancora da registrare (dal 27/10), "Ogni mese" per tipo di spesa e in fondo le
 * altre liste (già pagate, tolte dall'elenco). Ricerca, ordina, filtra,
 * "Prossimi addebiti" e le spese fisse ferme non si mostrano più qui (il codice resta).
 *
 * Com'era il 7/10: risponde a "cosa dobbiamo pagare, e cosa è già scaduto?".
 *
 * Prima, a 390px: una frase di tre righe, tre riquadri di numeri, poi schede
 * alte mezzo schermo (TIPO DI SPESA / QUANDO SCADE / IMPORTO e i bottoni) e la
 * barra "Nuova spesa": si vedeva una spesa per volta.
 * Qui:
 *   1. in grande, quanto è già scaduto, e quante di quelle in banca
 *      risultano già pagate (basta confermarle);
 *   2. le viste: da pagare, spese fisse, già pagate;
 *   3. da pagare: prima quelle che in banca risultano già pagate, poi le
 *      scadute, poi quelle in arrivo. Una riga per spesa, con l'icona del tipo
 *      (fattura, spesa fissa, contanti); si tocca e sale il pannello con i
 *      gesti di prima: conferma il pagamento, segna pagata, togli dall'elenco;
 *   4. spese fisse: dove vanno i soldi ogni mese (una riga per categoria, che
 *      si apre), i prossimi addebiti, le auto a rate;
 *   5. già pagate: per mese, la più recente in cima.
 * "Nuova spesa" resta in fondo: registrare un'uscita in contanti si fa anche
 * in giro. Stesse chiamate, stessa cache e stessi parametri nell'indirizzo del
 * computer (vista, status, q, ordina, solo, tipo, mese, storico).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/auth.store';
import { Icon, type IconName } from '../../../components/ui/Icon';
import { OrdinaFiltra, perData, perNumero, perTesto, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina } from '../../../components/ui/OrdinaFiltra';
import { MEmpty, MGroup, MHero, MMore, MPage, MRow, MSearch, MSeg, leggibile } from '../../../components/mobile/MobileKit';
import { NewRecurringModal } from '../../../components/modals/NewRecurringModal';
import { useUrlState } from '../../../hooks/useUrlState';
import { fmtEur } from '../../../lib/utils';
import { SPESE_VISTE, adminHref, type SpeseVista } from '../../../lib/adminRoutes';
import { canReadAdmin, canWriteAdmin, isSuperAdmin, isoDay, payablesQuery, plurale, type MatchKind, type PayableRow } from '../adminApi';
import { useAdminActions } from '../useAdminActions';
import { SettlePopover } from '../SettlePopover';
import { MOTIVO_TOLTA_LABEL, TrashPopover } from '../TrashPopover';
import { AdminHeader, useStorico, useVista } from '../shared/AdminShell';
import { Fatto, Foglio, Gesto, StoricoAcceso, Testo, aGruppi, euro, euroEsatto, euroRiga, giornoBreve, meseGruppo, nomeRiga } from '../shared/AdminMobile';
import {
  CATEGORIA_SPESA_LABEL, METODO_LABEL, OGNI_QUANTO, TIPO_SPESA_LABEL, comeAddebitato, daPagareQuery, durata, giorniDaOggi,
  meseInParole, metodoDaOperazione, quando, speseFisseQuery, spesePagateQuery,
  type AutoARate, type DaPagare, type SpesaFissa, type SpesaPagata, type SpesaRow, type SuggerimentoSpesa, type TipoSpesa,
} from '../fatture/fattureApi';
import { giornoDellaSpesa, useAzioniSpesaFissa } from './SpeseFisse';
import { divideSpese } from './SpeseDaPagareOra';
import { SpeseDaRegistrareMobile } from './SpeseDaRegistrare';

const PAGINA = 25;
/** Quante righe mostra un gruppo prima di "Vedi tutte". */
const PRIME = 6;

const ICONA_TIPO: Record<TipoSpesa, IconName> = { FORNITORE: 'receipt', SPESA_FISSA: 'repeat', CONTANTI: 'banknote' };

const NOTA_CONFERMA = 'Confermata da Spese da pagare: in banca c\'è il pagamento corrispondente.';

/** Il movimento in banca accanto a una spesa, con i campi che l'API aggiunge dal 24/9 (come in SpeseDaPagare). */
type Movimento = SuggerimentoSpesa & { dubbio?: string | null; scegli?: boolean };
const movimentoDi = (r: SpesaRow): Movimento | null => (r.suggerimento as Movimento | null) ?? null;
const SOURCE_MATCH: Record<SpesaRow['source'], MatchKind | null> = { PAYABLE: 'PAYABLE_PAYMENT', RECURRING: null, CASH: 'CASH_ENTRY' };

/** "fattura n. 2437 · Diritto chiamata", "rata 2 di 4 della fattura n. 42": cos'è, in una riga. */
function descrizione(r: SpesaRow): string | null {
  if (r.tipo === 'FORNITORE') {
    const rata = r.ofTotal && r.ofTotal > 1 && r.seq ? `rata ${r.seq} di ${r.ofTotal} della ` : '';
    const numero = r.number ? `${rata}fattura n. ${r.number}` : rata ? `${rata}fattura` : null;
    return [numero, r.description].filter(Boolean).join(' · ') || null;
  }
  return r.description && r.description !== r.counterparty ? r.description : null;
}

type Az = { kind: 'paga' | 'togli'; r: SpesaRow };

export function SpeseMobile({ nuovaSpesa }: { nuovaSpesa?: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteAdmin(user);
  const [vista, setVista] = useVista<SpeseVista>(SPESE_VISTE);
  const [status, setStatus] = useUrlState<string>('status', '');
  const storico = useStorico();
  // Lo storico vale solo dove c'è una lista di documenti: le spese fisse sono tutte attuali.
  const conStorico = vista !== 'fisse';
  const { data } = useQuery({ ...daPagareQuery(storico.on), enabled: canReadAdmin(user) });

  if (!canReadAdmin(user)) {
    return (
      <div className="page adm-page">
        <AdminHeader title="Spese" />
        <MPage><MEmpty>Le spese non sono per il tuo profilo: per vederle serve l'accesso all'Amministrazione.</MEmpty></MPage>
      </div>
    );
  }

  // 26/10/2026, "alleggerire": niente più le tre pillole. Una pagina sola come sul computer:
  // da pagare, addebiti automatici da spuntare, il mese per tipo di spesa. Le spese già pagate
  // si aprono da "Altre spese" (stesso `?vista=pagate`) e si torna indietro con un tocco.
  const indietro = (
    <button type="button" className="amk-indietro" onClick={() => { setStatus(''); setVista('dapagare'); }}>
      <Icon name="chevronL" size={14} /> Cosa c'è da pagare
    </button>
  );

  return (
    <div className="page adm-page">
      <AdminHeader title="Spese" menu={conStorico ? [storico.menuItem] : []} />
      <MPage>
        {data && vista !== 'pagate' && <Risposta d={data} />}
        {conStorico && storico.on && <StoricoAcceso year={storico.year} onOff={() => storico.set(false)} />}
        {vista !== 'pagate' && status === 'tolte' && <DaPagare d={data} viste={indietro} canWrite={canWrite} onFisse={() => setVista('fisse')} />}
        {vista !== 'pagate' && status !== 'tolte' && (
          <>
            <DaPagare d={data} viste={null} canWrite={canWrite} onFisse={() => setVista('fisse')} />
            {/* Le fatture arrivate su FattureInCloud e non ancora registrate (27/10/2026), come sul computer. */}
            <SpeseDaRegistrareMobile />
            <Fisse viste={null} canWrite={canWrite} incorporata />
            <MGroup title="Altre spese">
              <MRow icon="check" title="Spese già pagate" sub="per mese, la più recente in cima" onClick={() => setVista('pagate')} />
              <MRow icon="trash" title="Fatture dei fornitori tolte dall'elenco" sub="doppioni, errori" onClick={() => setStatus('tolte')} />
            </MGroup>
          </>
        )}
        {vista === 'pagate' && <Pagate viste={indietro} storico={storico.on} />}
      </MPage>
      {/* L'azione principale in fondo (mobile.md §4): un'uscita in contanti si registra anche in giro. */}
      {canWrite && nuovaSpesa && <div className="m-actionbar m-only">{nuovaSpesa}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// La risposta
// ════════════════════════════════════════════════════════════════════

function Risposta({ d }: { d: DaPagare }) {
  // 26/10/2026: la risposta è quello che c'è da pagare con un bonifico nostro. Gli addebiti
  // automatici passati non sono debiti: si dicono a parte, come cose da spuntare.
  const { daPagare, daSpuntare } = divideSpese(d);
  const totale = daPagare.reduce((s, r) => s + r.amount, 0);
  const scadute = daPagare.filter((r) => (giorniDaOggi(r.dueDate) ?? 0) < 0).length;
  if (daPagare.length > 0 || daSpuntare.length > 0) {
    return (
      <MHero
        label="Da pagare con un bonifico nostro"
        unit={totale > 0 ? '€' : undefined}
        value={totale > 0 ? fmtEur(totale) : 'Niente'}
        alert={scadute > 0}
        sub={<>
          {scadute > 0 ? <span className="is-alert">{plurale(scadute, 'già scaduta', 'già scadute')}</span> : plurale(daPagare.length, 'spesa', 'spese')}
          {daSpuntare.length > 0 && <> · {plurale(daSpuntare.length, 'addebito automatico da spuntare', 'addebiti automatici da spuntare')}</>}
        </>}
      />
    );
  }
  return <RispostaVecchia d={d} />;
}

/** La risposta di prima (7/10/2026), quando non c'è niente né da pagare né da spuntare. */
function RispostaVecchia({ d }: { d: DaPagare }) {
  const t = d.totals;
  const inBanca = d.items.filter((r) => r.suggerimento).length;
  if (t.scadute.amount > 0) {
    const cosa = [
      t.scadute.fatture ? plurale(t.scadute.fatture, 'fattura di un fornitore', 'fatture di fornitori') : null,
      t.scadute.fisse ? plurale(t.scadute.fisse, 'spesa fissa', 'spese fisse') : null,
      t.scadute.contanti ? plurale(t.scadute.contanti, 'uscita in contanti', 'uscite in contanti') : null,
    ].filter(Boolean).join(', ');
    return (
      <MHero
        label="Spese scadute"
        unit="€"
        value={fmtEur(t.scadute.amount)}
        alert
        sub={<>{cosa}{inBanca > 0 && <> · {inBanca === 1 ? 'una in banca risulta già pagata' : `${inBanca} in banca risultano già pagate`}</>}</>}
      />
    );
  }
  if (t.prossimi.amount > 0) {
    return <MHero label={`Da pagare entro il ${giornoBreve(d.entro)}`} unit="€" value={fmtEur(t.prossimi.amount)} sub="nessuna spesa scaduta" />;
  }
  return <MHero label="Spese da pagare" value="Niente" sub="nessuna fattura di fornitori o spesa fissa in arrivo" />;
}

// ════════════════════════════════════════════════════════════════════
// Da pagare
// ════════════════════════════════════════════════════════════════════

const ORDINI: OpzioneOrdina<SpesaRow>[] = [
  { key: 'scadenza', label: 'Scadenza più vicina prima', criteri: [perData((r) => r.dueDate), perTesto((r) => r.counterparty)] },
  { key: 'costose', label: 'Prima le spese più costose', criteri: [perNumero((r) => r.amount, 'decrescente'), perData((r) => r.dueDate)] },
  { key: 'economiche', label: 'Prima le spese meno costose', criteri: [perNumero((r) => r.amount), perData((r) => r.dueDate)] },
  { key: 'fornitore', label: 'A chi paghiamo, dalla A alla Z', criteri: [perTesto((r) => r.counterparty), perData((r) => r.dueDate)] },
];

/** Gli stessi filtri e gli stessi parametri del computer (`solo`, `tipo`). */
const FILTRI: FiltroDef<SpesaRow>[] = [
  {
    param: 'solo', label: 'Quali spese',
    opzioni: [
      { value: 'scadute', label: 'Solo le spese scadute' },
      { value: 'entro30', label: 'Solo quelle che scadono entro 30 giorni' },
      { value: 'banca', label: 'Solo quelle che in banca risultano già pagate' },
    ],
    test: (r, v) => (v === 'scadute' ? r.gruppo === 'SCADUTE' : v === 'entro30' ? r.gruppo === 'PROSSIMI' : !!r.suggerimento),
  },
  {
    param: 'tipo', label: 'Tipo di spesa', multi: true,
    opzioni: [
      { value: 'FORNITORE', label: 'Fatture dei fornitori' },
      { value: 'SPESA_FISSA', label: 'Spese fisse' },
      { value: 'CONTANTI', label: 'Uscite in contanti' },
    ],
    test: (r, v) => r.tipo === v,
  },
];

function DaPagare({ d, viste, canWrite, onFisse }: { d: DaPagare | undefined; viste: ReactNode; canWrite: boolean; onFisse: () => void }) {
  const [status, setStatus] = useUrlState<string>('status', '');
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const ordina = useOrdina(ORDINI);
  const filtri = useFiltri(FILTRI, d?.items);
  const [sel, setSel] = useState<SpesaRow | null>(null);
  const [az, setAz] = useState<Az | null>(null);
  const [aperti, setAperti] = useState<Record<string, boolean>>({});
  const { pay, ignoreDocs } = useAdminActions();

  if (status === 'tolte') return <Tolte viste={viste} canWrite={canWrite} onBack={() => setStatus('')} />;
  if (!d) return <>{viste}<MGroup title="Da pagare"><MEmpty>Carico le spese da pagare…</MEmpty></MGroup></>;

  const s = search.trim().toLowerCase();
  const items = ordina.applica(filtri.applica(d.items.filter((r) => !s || `${r.counterparty} ${r.description ?? ''} ${r.number ?? ''}`.toLowerCase().includes(s))));
  // 26/10/2026: due gruppi come sul computer (SpeseDaPagareOra): quello che paghiamo noi e gli
  // addebiti automatici già passati, che vanno solo spuntati. Gli automatici in arrivo non si
  // ripetono: stanno nel calendario e nel gruppo "Ogni mese" qui sotto.
  const diviso = divideSpese({ ...d, items });
  const gruppi = [
    { key: 'pagare', titolo: 'Da pagare', righe: diviso.daPagare, note: 'Fatture dei fornitori e spese che non partono da sole.' },
    { key: 'spuntare', titolo: 'Addebiti automatici da spuntare', righe: diviso.daSpuntare, note: 'Partono da soli: va solo segnato che sono pagati. Tocca la riga e conferma.' },
  ].filter((g) => g.righe.length > 0);

  const riga = (r: SpesaRow) => {
    const mov = movimentoDi(r);
    const g = giorniDaOggi(r.dueDate);
    const tempo = r.gruppo === 'SCADUTE' && g != null && g < 0
      ? <span className="is-alert">da {durata(-g)}</span>
      : quando(r.dueDate, 'scaduta');
    const cosa = descrizione(r);
    return (
      <MRow
        key={r.key}
        icon={ICONA_TIPO[r.tipo]}
        title={nomeRiga(r.counterparty)}
        // Nel gruppo "già pagate in banca" la data è quella del movimento; accanto
        // cos'è, perché due righe di HighLevel senza descrizione sono uguali.
        sub={mov
          ? `${giornoBreve(mov.settledOn)} · ${cosa ?? (mov.tipo === 'BANCA' ? 'pagamento' : 'addebito')}`
          : <>{tempo}{cosa ? ` · ${cosa}` : ''}</>}
        trail={euroRiga(r.amount)}
        onClick={() => setSel(r)}
      />
    );
  };

  return (
    <>
      {/* Ricerca, ordina e filtra tolti dalla vista il 26/10/2026 (alleggerire): le righe sono poche. */}
      {viste}

      {gruppi.length === 0 ? (
        <MGroup title="Da pagare">
          <MEmpty>{s || filtri.attivi ? 'Nessuna spesa da pagare con questa ricerca o questi filtri.' : 'Niente da pagare: fatture dei fornitori, spese fisse e contanti sono tutti in regola.'}</MEmpty>
        </MGroup>
      ) : gruppi.map((g) => {
        const tutte = aperti[g.key] || g.righe.length <= PRIME + 1;
        return (
          <MGroup key={g.key} title={g.titolo} count={g.righe.length} note={'note' in g ? g.note : undefined}>
            {(tutte ? g.righe : g.righe.slice(0, PRIME)).map(riga)}
            {g.righe.length > PRIME + 1 && (
              <MMore label={tutte ? 'Mostra meno' : `Vedi tutte le ${g.righe.length}`} onClick={() => setAperti((x) => ({ ...x, [g.key]: !x[g.key] }))} />
            )}
          </MGroup>
        );
      })}


      {sel && <FoglioSpesa r={sel} canWrite={canWrite} onClose={() => setSel(null)} onFisse={onFisse} onAz={(a) => { setSel(null); setAz(a); }} />}

      {az?.kind === 'paga' && (
        <SettlePopover
          mode="paga"
          amount={az.r.amount}
          subtitle={az.r.tipo === 'FORNITORE'
            ? (az.r.ofTotal && az.r.ofTotal > 1 && az.r.seq ? `rata ${az.r.seq} di ${az.r.ofTotal}, ${az.r.counterparty}` : az.r.counterparty)
            : az.r.description ?? az.r.counterparty}
          target={SOURCE_MATCH[az.r.source] ? { kind: SOURCE_MATCH[az.r.source]!, id: az.r.id } : undefined}
          counterparty={az.r.counterparty}
          side="DEBIT"
          withNote={az.r.source === 'PAYABLE'}
          pending={pay.isPending}
          onClose={() => setAz(null)}
          onConfirm={(v) => {
            const r = az.r;
            setAz(null);
            pay.mutate({ source: r.source, id: r.id, amount: r.amount, label: r.counterparty, dueDate: r.dueDate, ...v });
          }}
        />
      )}
      {az?.kind === 'togli' && (
        <TrashPopover
          title={az.r.number ?? az.r.counterparty}
          pending={ignoreDocs.isPending}
          onClose={() => setAz(null)}
          onConfirm={(v) => {
            const r = az.r;
            setAz(null);
            ignoreDocs.mutate({ kind: 'payable', ids: [r.refId], label: `${r.counterparty} · ${r.number ?? 's/n'}`, ...v });
          }}
        />
      )}
    </>
  );
}

/** Il pannello di una spesa: i fatti, il movimento in banca se c'è, e i gesti. */
function FoglioSpesa({ r, canWrite, onClose, onFisse, onAz }: {
  r: SpesaRow; canWrite: boolean; onClose: () => void; onFisse: () => void; onAz: (a: Az) => void;
}) {
  const navigate = useNavigate();
  const { confermaProposta, pay } = useAdminActions();
  const mov = movimentoDi(r);
  const scaduta = r.gruppo === 'SCADUTE';

  const conferma = () => {
    if (!mov) return;
    onClose();
    if (mov.tipo === 'BANCA') {
      confermaProposta.mutate({ lato: 'USCITA', rataId: r.id, txId: mov.txId, paidDate: mov.settledOn ?? r.dueDate ?? '', label: r.counterparty, importo: r.amount, nota: NOTA_CONFERMA });
    } else {
      // Si registra l'importo VERO addebitato (lo fa l'API con il movimento collegato), come sul computer.
      pay.mutate({
        source: 'RECURRING', id: r.id, dueDate: r.dueDate, amount: mov.amount, label: r.counterparty,
        paidDate: mov.settledOn ?? r.dueDate ?? '', method: metodoDaOperazione(mov.operationType), bankTransactionId: mov.txId, accountId: null,
      });
    }
  };
  const scegli = () => {
    onClose();
    navigate(adminHref('banca', { vista: 'movimenti', q: mov?.counterparty ?? r.counterparty, side: 'DEBIT', unmatched: '1' }));
  };

  const primary = !canWrite ? undefined : mov ? (
    mov.scegli
      ? <button className="btn btn-primary m-block" onClick={scegli}><Icon name="link" size={12} /> Scegli il movimento in banca</button>
      : <button className="btn btn-primary m-block" disabled={confermaProposta.isPending || pay.isPending} onClick={conferma}><Icon name="check" size={12} /> Conferma il pagamento</button>
  ) : (
    <button className="btn btn-primary m-block" onClick={() => onAz({ kind: 'paga', r })}><Icon name="check" size={12} /> Segna pagata</button>
  );
  const cosa = descrizione(r);

  return (
    <Foglio open onClose={onClose} title={leggibile(r.counterparty)} primary={primary}>
      <div className="mk-list">
        <Fatto label="Cos'è" value={TIPO_SPESA_LABEL[r.tipo]} />
        {cosa && <Fatto label="Per" value={cosa} />}
        <Fatto label="Scadenza" alert={scaduta} value={<>{giornoBreve(r.dueDate) || 'senza data'}<span className="s">{quando(r.dueDate, 'scaduta')}</span></>} />
        <Fatto label="Importo" value={euroEsatto(r.amount)} />
        {r.method && <Fatto label="Come si paga" value={METODO_LABEL[r.method]} />}
        {r.entity === 'PERSONALE_ANDREA' && <Fatto label="Di chi" value="Di Andrea, non della società" />}
      </div>

      {mov && (
        <div className="mk-list">
          <Testo>
            {mov.dubbio ?? <>In banca risulta pagata {comeAddebitato(mov.operationType)} il {giornoBreve(mov.settledOn)}: <strong>{euroEsatto(mov.amount)}</strong> a {leggibile(mov.counterparty ?? mov.label)}.</>}
          </Testo>
        </div>
      )}

      <div className="mk-list">
        {canWrite && mov && <Gesto icon="check" label="Segna pagata in un altro modo" onClick={() => onAz({ kind: 'paga', r })} />}
        {r.source === 'RECURRING' && <Gesto icon="repeat" label="Apri le spese fisse" sub="la spesa fissa si cambia, sospende o termina da lì" onClick={() => { onClose(); onFisse(); }} />}
        {canWrite && r.source === 'PAYABLE' && <Gesto icon="trash" label="Togli dall'elenco" danger onClick={() => onAz({ kind: 'togli', r })} />}
      </div>
    </Foglio>
  );
}

function Tolte({ viste, canWrite, onBack }: { viste: ReactNode; canWrite: boolean; onBack: () => void }) {
  const user = useAuthStore((s) => s.user);
  const superAdmin = isSuperAdmin(user);
  const q = useQuery(payablesQuery({ status: 'cestinate' }));
  const { restoreDocs } = useAdminActions();
  const [sel, setSel] = useState<PayableRow | null>(null);
  const rows = (q.data ?? []).filter((p) => superAdmin || p.entity !== 'PERSONALE_ANDREA');
  return (
    <>
      {viste}
      <MGroup title="Fatture dei fornitori tolte" count={q.data ? rows.length : undefined} action={{ label: 'Da pagare', onClick: onBack }}
        note="Non entrano nei totali, ma su FattureInCloud restano.">
        {q.isLoading && !q.data ? <MEmpty>Carico le fatture tolte…</MEmpty>
          : rows.length === 0 ? <MEmpty>Nessuna fattura di fornitore tolta dall'elenco.</MEmpty>
          : rows.map((p) => (
            <MRow
              key={p.id}
              icon="receipt"
              title={nomeRiga(p.vendorName)}
              sub={`${p.number ?? 's/n'} · ${p.ignoredReason ? MOTIVO_TOLTA_LABEL[p.ignoredReason] : 'tolta dall\'elenco'}`}
              trail={<span className="amk-manca">{euroRiga(p.total)}</span>}
              onClick={() => setSel(p)}
            />
          ))}
      </MGroup>
      {sel && (
        <Foglio open onClose={() => setSel(null)} title={leggibile(sel.vendorName)}
          primary={canWrite ? (
            <button className="btn m-block" disabled={restoreDocs.isPending} onClick={() => { const p = sel; setSel(null); restoreDocs.mutate({ kind: 'payable', ids: [p.id], label: `${p.vendorName} · ${p.number ?? 's/n'}` }); }}>
              <Icon name="undo" size={12} /> Rimetti nell'elenco
            </button>
          ) : undefined}>
          <div className="mk-list">
            <Fatto label="Fattura" value={sel.number ?? 'senza numero'} />
            <Fatto label="Tolta il" value={giornoBreve(sel.ignoredAt) || '—'} />
            <Fatto label="Perché" value={sel.ignoredReason ? MOTIVO_TOLTA_LABEL[sel.ignoredReason] : '—'} />
            <Fatto label="Importo" value={euroEsatto(sel.total)} />
          </div>
          {sel.ignoredNote && <div className="mk-list"><Testo>{sel.ignoredNote}</Testo></div>}
        </Foglio>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Spese fisse
// ════════════════════════════════════════════════════════════════════

function Fisse({ viste, canWrite, incorporata }: {
  viste: ReactNode; canWrite: boolean;
  /** Dentro la pagina principale (26/10/2026): senza ricerca e senza "Prossimi addebiti", che stanno nel calendario. */
  incorporata?: boolean;
}) {
  const q = useQuery(speseFisseQuery);
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [cat, setCat] = useState<string | null>(null);
  const [voce, setVoce] = useState<SpesaFissa | null>(null);
  const [autoAperte, setAutoAperte] = useState(false);
  const [modifica, setModifica] = useState<SpesaFissa | null>(null);
  const d = q.data;
  const oggi = isoDay();

  const inVigore = useMemo(() => (d?.items ?? []).filter((v) => v.inVigore && !v.auto), [d]);
  const ferme = useMemo(() => (d?.items ?? []).filter((v) => !v.inVigore && !v.auto), [d]);
  const prossimi = useMemo(
    () => inVigore.filter((v) => v.nextDueDate && v.nextDueDate >= oggi).sort((a, b) => (a.nextDueDate ?? '').localeCompare(b.nextDueDate ?? '')).slice(0, 5),
    [inVigore, oggi],
  );

  if (q.isLoading && !d) return <>{viste}<MGroup title="Spese fisse"><MEmpty>Carico le spese fisse…</MEmpty></MGroup></>;
  if (!d) return <>{viste}<MGroup title="Spese fisse"><MEmpty>Le spese fisse non si sono caricate: riprova tra qualche secondo.</MEmpty></MGroup></>;

  const s = search.trim().toLowerCase();
  const trovate = s ? d.items.filter((v) => `${v.name} ${v.counterparty ?? ''} ${v.note ?? ''}`.toLowerCase().includes(s)) : [];
  const arretrate = inVigore.filter((v) => v.nextDueDate && v.nextDueDate < oggi).length;
  const mancanti = d.rateMancanti.filter((m) => m.addebiti > 0);
  const dellaCategoria = cat ? inVigore.filter((v) => v.category === cat).sort((a, b) => b.alMese - a.alMese) : [];

  // Niente icona: sono tutte spese fisse, la stessa icona su ogni riga non dice niente.
  // "al mese" sotto l'importo solo quando NON è mensile: "il 25 di ogni mese" lo dice già.
  const rigaVoce = (v: SpesaFissa, conData = false) => (
    <MRow
      key={v.id}
      title={v.name}
      sub={!v.inVigore ? (v.endDate ? `terminata il ${giornoBreve(v.endDate)}` : 'sospesa')
        : conData ? `${giornoBreve(v.nextDueDate)} · ${quando(v.nextDueDate, 'scaduta')}`
        : giornoDellaSpesa(v) || OGNI_QUANTO[v.cycle]}
      trail={euroRiga(v.amountGross)}
      trailSub={v.cycle !== 'MONTHLY' ? OGNI_QUANTO[v.cycle] : undefined}
      onClick={() => { setCat(null); setVoce(v); }}
    />
  );

  return (
    <>
      {!incorporata && (
        <div className="amk-viste">
          {viste}
          <MSearch value={search} onChange={setSearch} placeholder="Cerca una spesa fissa o un fornitore" />
        </div>
      )}

      {s ? (
        <MGroup title="Spese fisse trovate" count={trovate.length}>
          {trovate.length === 0 ? <MEmpty>{`Nessuna spesa fissa con "${search.trim()}".`}</MEmpty> : trovate.map((v) => rigaVoce(v))}
        </MGroup>
      ) : (
        <>
          <MGroup title="Ogni mese" note={`In tutto ${euro(d.totals.monthly)} al mese, auto a rate comprese. Quelle annuali pesano per un dodicesimo.`}>
            {d.groups.map((g) => (
              <MRow
                key={g.category}
                title={CATEGORIA_SPESA_LABEL[g.category] ?? g.category}
                sub={plurale(g.count, 'spesa', 'spese')}
                trail={euro(g.monthly)}
                trailSub="al mese"
                onClick={() => setCat(g.category)}
              />
            ))}
            {d.auto.length > 0 && (
              <MRow
                title="Auto pagate a rate"
                sub={mancanti.length > 0
                  ? <span className="is-alert">{plurale(mancanti.reduce((n, m) => n + m.addebiti, 0), 'rata in banca non registrata', 'rate in banca non registrate')}</span>
                  : plurale(d.auto.length, 'auto', 'auto')}
                trail={euro(d.totals.autoMonthly)}
                trailSub="al mese"
                onClick={() => setAutoAperte(true)}
              />
            )}
          </MGroup>

          {!incorporata && prossimi.length > 0 && (
            <MGroup title="Prossimi addebiti"
              note={arretrate > 0 ? `${arretrate === 1 ? 'Una spesa fissa ha' : `${arretrate} spese fisse hanno`} un addebito passato non ancora segnato pagato: si conferma da "Da pagare".` : undefined}>
              {prossimi.map((v) => rigaVoce(v, true))}
            </MGroup>
          )}

          {!incorporata && ferme.length > 0 && (
            <MGroup title="Sospese o terminate" count={ferme.length}>
              {ferme.map((v) => rigaVoce(v))}
            </MGroup>
          )}
        </>
      )}

      {cat && (
        <Foglio open onClose={() => setCat(null)} title={CATEGORIA_SPESA_LABEL[cat] ?? cat}>
          <div className="mk-list">{dellaCategoria.map((v) => rigaVoce(v))}</div>
        </Foglio>
      )}

      {voce && <FoglioVoce v={voce} canWrite={canWrite} onClose={() => setVoce(null)} onModifica={(v) => { setVoce(null); setModifica(v); }} />}

      {autoAperte && (
        <FoglioAuto auto={d.auto} mancanti={mancanti} canWrite={canWrite} onClose={() => setAutoAperte(false)}
          onModifica={(id) => { setAutoAperte(false); setModifica(d.items.find((v) => v.id === id) ?? null); }} />
      )}

      {modifica && createPortal(<NewRecurringModal recurring={modifica} onClose={() => setModifica(null)} />, document.body)}
    </>
  );
}

function FoglioVoce({ v, canWrite, onClose, onModifica }: { v: SpesaFissa; canWrite: boolean; onClose: () => void; onModifica: (v: SpesaFissa) => void }) {
  const { sospendi, termina, riattiva } = useAzioniSpesaFissa();
  const scaduta = v.inVigore && !!v.nextDueDate && v.nextDueDate < isoDay();
  return (
    <Foglio open onClose={onClose} title={v.name}
      primary={canWrite ? <button className="btn m-block" onClick={() => onModifica(v)}><Icon name="pencil" size={12} /> Modifica la spesa</button> : undefined}>
      <div className="mk-list">
        <Fatto label="Quanto costa" value={<>{euroEsatto(v.amountGross)} {OGNI_QUANTO[v.cycle]}{v.cycle !== 'MONTHLY' && <span className="s">cioè {euroEsatto(v.alMese)} al mese</span>}</>} />
        {giornoDellaSpesa(v) && <Fatto label="Quando si paga" value={giornoDellaSpesa(v)} />}
        {v.inVigore
          ? <Fatto label="Prossimo addebito" alert={scaduta} value={<>{giornoBreve(v.nextDueDate) || '—'}{v.nextDueDate && <span className="s">{quando(v.nextDueDate, 'scaduta')}</span>}</>} />
          : <Fatto label="Stato" value={v.endDate ? `Terminata il ${giornoBreve(v.endDate)}` : 'Sospesa'} />}
        <Fatto label="Come si paga" value={v.method ? METODO_LABEL[v.method] : 'non indicato'} />
        {v.counterparty && v.counterparty !== v.name && <Fatto label="Fornitore" value={leggibile(v.counterparty)} />}
        <Fatto label="Categoria" value={CATEGORIA_SPESA_LABEL[v.category] ?? v.category} />
      </div>
      {v.note && <div className="mk-list"><Testo>{v.note}</Testo></div>}
      {canWrite && (
        <div className="mk-list">
          {v.inVigore ? (
            <>
              <Gesto icon="clock" label="Sospendi per ora" sub="non compare fra le spese da pagare finché non la riattivi" onClick={() => { onClose(); sospendi.mutate(v); }} />
              <Gesto icon="x" label="Termina: non la paghiamo più" danger onClick={() => { onClose(); termina.mutate(v); }} />
            </>
          ) : (
            <Gesto icon="repeat" label="Riattiva la spesa fissa" onClick={() => { onClose(); riattiva.mutate(v); }} />
          )}
        </div>
      )}
    </Foglio>
  );
}

function FoglioAuto({ auto, mancanti, canWrite, onClose, onModifica }: {
  auto: AutoARate[]; mancanti: Array<{ recurringId: string; nome: string; addebiti: number; totaleAddebiti: number; dallaBanca: string | null; dalGestionale: string | null }>;
  canWrite: boolean; onClose: () => void; onModifica: (recurringId: string) => void;
}) {
  const { creaRateMancanti } = useAdminActions();
  return (
    <Foglio open onClose={onClose} title="Auto pagate a rate">
      <div className="mk-list">
        {auto.map((a) => {
          const rec = a.recurring;
          return (
            <MRow
              key={a.payableId}
              icon="repeat"
              title={nomeRiga(a.vendorName)}
              sub={rec ? `rata ${euroEsatto(rec.monthly)} al mese · ${rec.inVigore ? `prossima ${quando(rec.nextDueDate, 'scaduta')}` : 'rata ferma'}` : 'rata non collegata'}
              trail={euro(a.financed)}
              trailSub="finanziati"
              onClick={canWrite && rec ? () => onModifica(rec.id) : undefined}
              chevron={false}
            />
          );
        })}
      </div>
      <div className="mk-list">
        <Testo>Il fornitore dell'auto l'ha già pagato la finanziaria: noi paghiamo la rata ogni mese. Per questo il valore dell'auto non è fra le spese da pagare.{canWrite ? ' Tocca un\'auto per modificare la rata.' : ''}</Testo>
      </div>
      {mancanti.length > 0 && (
        <MGroup title="Rate in banca non registrate">
          {mancanti.map((m) => (
            <Gesto
              key={m.recurringId}
              icon="plus"
              label={`Aggiungi ${m.addebiti === 1 ? 'la rata' : `le ${m.addebiti} rate`} di ${m.nome}`}
              sub={`In banca partono ${m.dallaBanca ? `dal ${giornoBreve(m.dallaBanca)}` : 'da prima'}, qui ${m.dalGestionale ? `dal ${giornoBreve(m.dalGestionale)}` : 'mancano'}: ${euro(m.totaleAddebiti)} in tutto`}
              disabled={!canWrite || creaRateMancanti.isPending}
              onClick={() => creaRateMancanti.mutate({ recurringId: m.recurringId, nome: m.nome, addebiti: m.addebiti, totale: m.totaleAddebiti })}
            />
          ))}
        </MGroup>
      )}
    </Foglio>
  );
}

// ════════════════════════════════════════════════════════════════════
// Già pagate
// ════════════════════════════════════════════════════════════════════

/** Gli stessi ordini e filtri del computer: li fa l'API (`sort`, `tipo`). */
const ORDINI_PAGATE: OpzioneOrdina[] = [
  { key: 'recenti', label: 'Pagate più di recente prima' },
  { key: 'vecchie', label: 'Pagate da più tempo prima' },
  { key: 'costose', label: 'Prima le spese più costose' },
  { key: 'nome', label: 'A chi abbiamo pagato, dalla A alla Z' },
];
const FILTRI_PAGATE: FiltroDef[] = [
  {
    param: 'tipo', label: 'Tipo di spesa', multi: true,
    opzioni: [
      { value: 'FORNITORE', label: 'Fatture dei fornitori' },
      { value: 'SPESA_FISSA', label: 'Spese fisse' },
      { value: 'CONTANTI', label: 'Uscite in contanti' },
    ],
  },
];

function Pagate({ viste, storico }: { viste: ReactNode; storico: boolean }) {
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [mese, setMese] = useUrlState<string>('mese', '');
  const ordina = useOrdina(ORDINI_PAGATE);
  const filtri = useFiltri(FILTRI_PAGATE);
  const [quante, setQuante] = useState(PAGINA);
  const q = useQuery({
    ...spesePagateQuery({ search, month: mese || undefined, includeHistory: storico, page: 1, pageSize: quante, sort: ordina.key, tipo: filtri.valori.tipo }),
    placeholderData: keepPreviousData,
  });
  const items = q.data?.items ?? [];
  const total = q.data?.total ?? 0;
  const perMese = ordina.key === 'recenti' || ordina.key === 'vecchie';
  const gruppi = perMese ? aGruppi(items, (r) => (r.paidDate ?? '').slice(0, 7)) : [{ chiave: 'tutte', items }];

  const riga = (r: SpesaPagata) => {
    const dettaglio = [r.number ? `fattura n. ${r.number}` : null, r.description].filter(Boolean).join(' · ');
    return (
      <MRow
        key={r.key}
        icon={ICONA_TIPO[r.tipo]}
        title={nomeRiga(r.counterparty)}
        sub={`il ${giornoBreve(r.paidDate)}${dettaglio ? ` · ${dettaglio}` : ''}`}
        trail={euroRiga(r.amount)}
      />
    );
  };

  return (
    <>
      <div className="amk-viste">
        {viste}
        <div className="amk-tools">
          <MSearch value={search} onChange={setSearch} placeholder="Fornitore o spesa" />
          <OrdinaFiltra ordina={ordina} filtri={filtri} label="Filtra" />
        </div>
      </div>

      {mese && (
        <div className="mk-list">
          <MRow icon="calendar" title={`Solo le spese pagate a ${meseInParole(mese)}`} sub="Tocca per vederle tutte" onClick={() => setMese('')} chevron={false} />
        </div>
      )}

      {q.isLoading && !q.data ? (
        <MGroup title="Già pagate"><MEmpty>Carico le spese pagate…</MEmpty></MGroup>
      ) : items.length === 0 ? (
        <MGroup title="Già pagate">
          <MEmpty>{search.trim() || mese || filtri.attivi ? 'Nessuna spesa pagata con questi filtri.' : 'Nessuna spesa pagata, per ora.'}</MEmpty>
        </MGroup>
      ) : gruppi.map((g, i) => (
        <MGroup key={g.chiave} title={perMese ? meseGruppo(g.chiave) : ordina.attiva.label} count={g.items.length}>
          {g.items.map(riga)}
          {i === gruppi.length - 1 && total > items.length && (
            <MMore label={`Vedi altre ${Math.min(PAGINA, total - items.length)}`} onClick={() => setQuante((n) => n + PAGINA)} />
          )}
        </MGroup>
      ))}
    </>
  );
}
